import { useCallback, useContext, useMemo } from 'react';
import { useSwitchNetwork } from 'wagmi';

import { Web3ChainStatusContext } from '../contexts';
import { CHAIN_ID_TO_KEY_MAP, WEB3_CONFIG } from '../lib';
import { saveChainToSs } from '../utils';
import { useWeb3State } from './use-web3-state';

export const useSwitchChain = (
  ...args: Parameters<typeof useSwitchNetwork>
) => {
  const { updateChain } = useWeb3State();
  const { setIsSwitching } = useContext(Web3ChainStatusContext);
  const { switchNetworkAsync: coreSwitchChainAsync, ...rest } =
    useSwitchNetwork(...args);

  const switchNetworkAsync = useCallback(
    async (
      chainId: Parameters<NonNullable<typeof coreSwitchChainAsync>>[0],
    ) => {
      const targetChainKey = CHAIN_ID_TO_KEY_MAP[chainId!];

      if (!targetChainKey) {
        throw Error('Attempt to switch to unsupported chain');
      }

      const targetChain = WEB3_CONFIG.chains[targetChainKey];

      try {
        setIsSwitching(true);

        /**
         * Some wallets (like metamask mobile browser) refresh the page
         * immediately without leaving time to update the chain in storage
         */
        saveChainToSs(targetChain.id);
        await coreSwitchChainAsync?.(targetChain.id);
        await updateChain(targetChain);
        setIsSwitching(false);
      } catch (err) {
        setIsSwitching(false);

        throw err;
      }
    },
    [coreSwitchChainAsync],
  );

  return useMemo(
    () => ({ switchNetworkAsync, ...rest }),
    [switchNetworkAsync, rest],
  );
};
